import React, { useMemo } from 'react';
import { DatePicker, Radio } from 'antd';
import { Column, Label, RowCentered } from '@ydistri/ds';
import { AsoScenarioConfiguration, AsoScenarioSpecialPeriodMode } from '@ydistri/api-sdk';
import dayjs, { Dayjs } from 'dayjs';
import { useAsoConfigurations } from '../useAsoConfigurations';
import { AsoNumericFieldRow } from '../components/AsoNumericFieldRow';
import { AsoSliderInputRow } from '../components/AsoSliderInputRow';
import { LabelFieldWrapper, TextWithTooltip } from '../components/asoSettingsStyledComponents';
import AsoTooltip from '../../AsoTooltip';

const { RangePicker } = DatePicker;

const specialPeriodOptions = [
  { label: 'None', value: AsoScenarioSpecialPeriodMode.None },
  { label: 'Months', value: AsoScenarioSpecialPeriodMode.Months },
  { label: 'Date range', value: AsoScenarioSpecialPeriodMode.Range },
];

const SpecialPeriod: React.FC = () => {
  const { config, handleChange, debouncedHandleChange } = useAsoConfigurations();

  const rangeValue: [Dayjs | null, Dayjs | null] = useMemo(() => {
    if (!config?.specialPeriodFrom && !config?.specialPeriodTo) return [null, null];
    const from = config.specialPeriodFrom ? dayjs(config.specialPeriodFrom) : null;
    const to = config.specialPeriodTo ? dayjs(config.specialPeriodTo) : null;
    return [from, to];
  }, [config]);

  const handleModeChange = (nextMode: AsoScenarioSpecialPeriodMode) => {
    const patch: Partial<AsoScenarioConfiguration> = {
      specialPeriodMode: nextMode, // specialPeriodValueMap[nextMode],
    };

    switch (nextMode) {
      case AsoScenarioSpecialPeriodMode.None:
        patch.specialPeriodMonths = null;
        patch.specialPeriodFrom = null;
        patch.specialPeriodTo = null;
        break;
      case AsoScenarioSpecialPeriodMode.Months:
        patch.specialPeriodFrom = null;
        patch.specialPeriodTo = null;
        break;
      case AsoScenarioSpecialPeriodMode.Range:
        patch.specialPeriodMonths = null;
        break;
      default:
        throw new Error(`Unknown special period mode.`);
    }

    handleChange(patch);
  };

  if (!config) return null;

  return (
    <Column $gap="0.5rem">
      <RowCentered $gap="0.25rem">
        <Label>Special period</Label>
        <AsoTooltip tooltipKey="specialPeriodMode" />
      </RowCentered>
      <Radio.Group
        options={specialPeriodOptions}
        value={config.specialPeriodMode}
        onChange={e => {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- options are only AsoScenarioSpecialPeriodMode
          handleModeChange(e.target.value as AsoScenarioSpecialPeriodMode);
        }}
        optionType="button"
        buttonStyle="solid"
      />
      {config.specialPeriodMode === AsoScenarioSpecialPeriodMode.Months && (
        <AsoNumericFieldRow
          label="Months"
          value={config.specialPeriodMonths ?? undefined}
          tooltipKey="specialPeriodMonths"
          onChange={v =>
            debouncedHandleChange({ specialPeriodMonths: typeof v === 'number' ? v : null })
          }
          postfix={null}
        />
      )}
      {config.specialPeriodMode === AsoScenarioSpecialPeriodMode.Range && (
        <LabelFieldWrapper>
          <TextWithTooltip as="span" $width="15rem">
            Date range
            <AsoTooltip tooltipKey="specialPeriodRange" />
          </TextWithTooltip>
          <RangePicker
            value={rangeValue}
            onChange={(vals: [Dayjs | null, Dayjs | null] | null) => {
              const [from, to] = vals ?? [null, null];
              handleChange({
                specialPeriodFrom: from ? from.format('YYYY-MM-DD') : null,
                specialPeriodTo: to ? to.format('YYYY-MM-DD') : null,
              });
            }}
          />
        </LabelFieldWrapper>
      )}

      {config.specialPeriodMode !== AsoScenarioSpecialPeriodMode.None && (
        <AsoSliderInputRow
          label="Special period weight"
          value={config.specialPeriodWeight}
          tooltipKey="specialPeriodWeight"
          min={0}
          max={1}
          step={0.05}
          onChange={v =>
            debouncedHandleChange({
              specialPeriodWeight: typeof v === 'number' ? v : undefined,
            })
          }
        />
      )}
    </Column>
  );
};

export default SpecialPeriod;
